import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Animated,
  Easing,
  Pressable,
  Text,
  View,
  type ImageSourcePropType,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { ArrowRight, type LucideIcon } from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import { GlassButton } from "@louise/moon-ui/primitives/GlassButton";
import { KebabMenu } from "@louise/moon-ui/primitives/KebabMenu";
import {
  cardSurface,
  PANEL_ON_DARK_PRESSED_FILL,
} from "@louise/moon-ui/primitives/CardSurface";
import { semanticColors } from "@louise/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const heroReferenceBackground =
  require("@src/apps/moon/assets/images/homepage-review-hero-bg-v41.png") as ImageSourcePropType;

const HERO_ICON_BUTTON_STYLE = {
  width: 28,
  height: 28,
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

export type HeroMetric = {
  label: string;
  value: string;
  note?: string;
  onPress?: () => void;
  accessibilityLabel?: string;
};

export type HeroMenuAction = {
  icon: LucideIcon;
  label: string;
  /** Stable analytics identifier: the label is translated. */
  action: string;
  onPress: () => void;
  /** `destructive` for an action that ends or removes something. */
  tone?: "default" | "destructive";
};

export type HeroPrimaryAction = {
  label: string;
  onPress: () => void;
  icon?: LucideIcon;
};

/**
 * A cycle that has just ended, for a hero to announce.
 *
 * Every Home surface that can be standing when a cycle closes takes this, so the
 * ending is stated by whatever hero is already there rather than by a card
 * floating above it. The card that used to do this contradicted the hero beneath
 * it — "you went all the way through" over "0 of 3 steps complete".
 *
 * `primary` is the way into the cycle. There is deliberately no "Got it": the
 * announcement expires with its own window, and a patient who starts another
 * cycle moves onto that cycle's home immediately, which is the exit that
 * matters.
 */
export type HeroCycleEnding = {
  title: string;
  body: string;
  primary: { label: string; onPress: () => void };
};

export type HeroEyebrow = {
  label: string;
  icon?: LucideIcon;
};

type HeroProps = {
  title: string;
  summary: string;
  /** Small uppercase label rendered above the title. */
  eyebrow?: HeroEyebrow;
  metrics?: HeroMetric[];
  metricVariant?: "cards" | "tags";
  /** When false, `cards` metrics hug their content instead of stretching to
   *  fill the row (use when there are only one or two metrics). Default: true. */
  metricsFillRow?: boolean;
  backgroundSource?: ImageSourcePropType;
  /** Loop a subtle drift animation on the background image. Default: true. */
  animateBackground?: boolean;
  /** Optional visual rendered to the left of the title and summary. */
  leading?: ReactNode;
  primaryAction?: HeroPrimaryAction;
  /**
   * A second, quieter button under the primary one.
   *
   * For a hero that owns two destinations of comparable weight — the post-cycle
   * home offers both the cycle that just ended and the next one — where a kebab
   * menu would hide the second behind a tap. Ignored unless `primaryAction` is
   * set: a lone secondary is just a primary drawn wrong.
   */
  secondaryAction?: HeroPrimaryAction;
  menuActions?: HeroMenuAction[];
  /**
   * When set, makes the hero's text area (eyebrow + title + summary) tappable
   * so the whole card acts as a shortcut. Kebab menu, metrics, primary action,
   * and bottomContent remain independent touch targets.
   */
  onPress?: () => void;
  onPressAccessibilityLabel?: string;
  /**
   * When true and both metrics + primaryAction are present, renders them
   * on the same horizontal row: metrics shrink to content width, primary
   * action takes the remaining space. Visually requires
   * `metricVariant="tags"` (pill-shaped metrics) to look right.
   */
  compactAction?: boolean;
  /** Custom content rendered inside the hero card, below the primary action. */
  bottomContent?: ReactNode;
  style?: StyleProp<ViewStyle>;
};

/**
 * Slim two-week-wait progress bar for the hero. Renders the elapsed share of
 * the wait (transfer → beta) so the day count is felt, not just read. Passed
 * to the hero via `bottomContent`.
 */
export function HeroWaitProgress({
  day,
  total,
  startLabel,
  endLabel,
}: {
  day: number;
  total: number;
  startLabel: string;
  endLabel: string;
}) {
  const pct = total > 0 ? Math.min(1, Math.max(0, day / total)) : 0;
  const [trackWidth, setTrackWidth] = useState(0);
  const reducedMotion = useReducedMotion();
  const shimmer = useRef(new Animated.Value(0)).current;
  const STREAK_WIDTH = 90;

  useEffect(() => {
    shimmer.setValue(0);
    if (reducedMotion || trackWidth <= 0) return undefined;
    // A light streak that sweeps the bar left→right on a continuous loop. It
    // starts and ends fully off-screen, so the loop reset is never visible —
    // the motion reads as continuous, which makes the wait feel alive.
    const loop = Animated.loop(
      Animated.timing(shimmer, {
        toValue: 1,
        duration: 1900,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [trackWidth, shimmer, reducedMotion]);

  const translateX = shimmer.interpolate({
    inputRange: [0, 1],
    outputRange: [-STREAK_WIDTH, trackWidth],
  });
  const endLabelStyle = {
    fontSize: 10,
    lineHeight: 13,
    color: semanticColors.onDark.text.tertiary,
    fontWeight: "800" as const,
    letterSpacing: 0.5,
    textTransform: "uppercase" as const,
  };
  return (
    <View style={{ marginTop: 16 }}>
      <View
        onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
        style={{
          height: 6,
          borderRadius: 999,
          backgroundColor: semanticColors.onDark.fill.strong,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            height: "100%",
            width: `${Math.round(pct * 100)}%`,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.85)",
          }}
        />
        {trackWidth > 0 ? (
          <Animated.View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              width: STREAK_WIDTH,
              transform: [{ translateX }],
            }}
          >
            <LinearGradient
              colors={[
                "rgba(255,255,255,0)",
                "rgba(255,255,255,0.9)",
                "rgba(255,255,255,0)",
              ]}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
              style={{ flex: 1 }}
            />
          </Animated.View>
        ) : null}
      </View>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 7,
        }}
      >
        <Text className="font-dm-sans" style={endLabelStyle}>
          {startLabel}
        </Text>
        <Text className="font-dm-sans" style={endLabelStyle}>
          {endLabel}
        </Text>
      </View>
    </View>
  );
}

function HeroMetricContent({ metric }: { metric: HeroMetric }) {
  return (
    <>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          lineHeight: 13,
          color: semanticColors.onDark.text.tertiary,
          fontWeight: "800",
          textTransform: "uppercase",
        }}
      >
        {metric.label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 18,
          color: semanticColors.onDark.text.primary,
          fontWeight: "800",
          marginTop: 3,
        }}
      >
        {metric.value}
      </Text>
      {metric.note ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: semanticColors.onDark.text.tertiary,
            fontWeight: "500",
            marginTop: 1,
          }}
        >
          {metric.note}
        </Text>
      ) : null}
    </>
  );
}

function HeroMetricPill({
  metric,
  fill = true,
}: {
  metric: HeroMetric;
  /** When false, the card hugs its content instead of stretching to fill the
   *  row. Used when there are only one or two metrics (e.g. the wait hero). */
  fill?: boolean;
}) {
  // Never the metric value: hero metrics carry clinical readings.
  const capturePress = usePressCapture("HeroMetricPill");
  const baseStyle = {
    ...cardSurface({ variant: "panel", tone: "onDark" }),
    ...(fill
      ? { flexGrow: 1, flexBasis: 96 }
      : { flexGrow: 0, flexShrink: 0, alignSelf: "flex-start" as const }),
    padding: 10,
  };

  if (metric.onPress) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(metric.label));
          metric.onPress?.();
        }}
        accessibilityRole="button"
        accessibilityLabel={metric.accessibilityLabel ?? metric.value}
        style={({ pressed }) => ({
          ...baseStyle,
          ...(pressed ? { backgroundColor: PANEL_ON_DARK_PRESSED_FILL } : null),
        })}
      >
        <HeroMetricContent metric={metric} />
      </Pressable>
    );
  }

  return (
    <View style={baseStyle}>
      <HeroMetricContent metric={metric} />
    </View>
  );
}

function HeroMetricTag({ metric }: { metric: HeroMetric }) {
  // Never the metric value: hero metrics carry clinical readings.
  const capturePress = usePressCapture("HeroMetricTag");
  const content = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "baseline",
          gap: 4,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            color: semanticColors.onDark.text.tertiary,
            fontSize: 10,
            lineHeight: 14,
            fontWeight: "700",
          }}
        >
          {metric.label}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: semanticColors.onDark.text.primary,
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "800",
          }}
        >
          {metric.value}
        </Text>
      </View>
      {metric.onPress ? (
        <ArrowRight
          size={13}
          color={semanticColors.onDark.text.primary}
          strokeWidth={2}
        />
      ) : null}
    </View>
  );
  const baseStyle = {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.24)",
    paddingHorizontal: 12,
    paddingVertical: 7,
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 6,
  };

  if (metric.onPress) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(metric.label));
          metric.onPress?.();
        }}
        accessibilityRole="button"
        accessibilityLabel={
          metric.accessibilityLabel ?? `${metric.label}: ${metric.value}`
        }
        style={({ pressed }) => ({
          ...baseStyle,
          backgroundColor: pressed
            ? semanticColors.onDark.fill.subtle
            : "transparent",
        })}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View
      style={baseStyle}
      accessibilityLabel={`${metric.label}: ${metric.value}`}
    >
      {content}
    </View>
  );
}

function HeroActionsMenu({ actions = [] }: { actions?: HeroMenuAction[] }) {
  const t = useTranslations();

  if (actions.length === 0) return null;

  return (
    <KebabMenu
      items={actions.map((item) => ({
        id: item.action,
        label: item.label,
        icon: item.icon,
        onPress: item.onPress,
        tone: item.tone,
        action: item.action,
      }))}
      accessibilityLabel={t("homepage.hero.open_hero_actions")}
      triggerAction="homepage.hero.open_hero_actions"
      buttonColor={semanticColors.onDark.text.secondary}
      triggerIconSize={18}
      triggerStyle={HERO_ICON_BUTTON_STYLE}
    />
  );
}

function HeroBackground({
  source = heroReferenceBackground,
  animate = true,
}: {
  source?: ImageSourcePropType;
  animate?: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const drift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reducedMotion || !animate) {
      drift.setValue(0);
      return undefined;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(drift, {
          toValue: 1,
          duration: 5200,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(drift, {
          toValue: 0,
          duration: 5200,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [animate, drift, reducedMotion]);

  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: "hidden",
        backgroundColor: "#23100D",
      }}
    >
      <Animated.Image
        source={source}
        resizeMode="cover"
        style={[
          {
            position: "absolute",
            left: animate ? "-7%" : 0,
            right: animate ? "-7%" : 0,
            top: animate ? "-10%" : 0,
            bottom: animate ? "-10%" : 0,
            width: animate ? "114%" : "100%",
            height: animate ? "120%" : "100%",
          },
          animate
            ? {
                transform: [
                  {
                    translateX: drift.interpolate({
                      inputRange: [0, 1],
                      outputRange: [-16, 18],
                    }),
                  },
                  {
                    translateY: drift.interpolate({
                      inputRange: [0, 1],
                      outputRange: [10, -12],
                    }),
                  },
                  {
                    scale: drift.interpolate({
                      inputRange: [0, 1],
                      outputRange: [1.03, 1.08],
                    }),
                  },
                ],
              }
            : null,
        ]}
      />
      <View
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          backgroundColor: "rgba(21,10,7,0.08)",
        }}
      />
    </View>
  );
}

function HeroEyebrowRow({ eyebrow }: { eyebrow: HeroEyebrow }) {
  const Icon = eyebrow.icon;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginBottom: 8,
      }}
    >
      {Icon ? (
        <Icon
          size={13}
          color={semanticColors.onDark.text.tertiary}
          strokeWidth={1.9}
        />
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          lineHeight: 14,
          color: semanticColors.onDark.text.tertiary,
          fontWeight: "800",
          letterSpacing: 0.6,
          textTransform: "uppercase",
        }}
      >
        {eyebrow.label}
      </Text>
    </View>
  );
}

function PrimaryActionButton({
  action,
  Icon,
  emphasis = "filled",
}: {
  action: HeroPrimaryAction;
  Icon?: LucideIcon;
  /** `outline` drops the fill so a second button reads as the quieter one. */
  emphasis?: "filled" | "outline";
}) {
  // The main hero action is the shimmer, the strongest glass; a second,
  // quieter action is the large frosted glass at the same height.
  const outline = emphasis === "outline";
  return (
    <GlassButton
      variant={outline ? "frosted" : "shimmer"}
      size="large"
      label={action.label}
      onPress={action.onPress}
      leading={
        Icon ? (
          <Icon
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        ) : undefined
      }
      trailing={
        Icon ? undefined : (
          <ArrowRight
            size={15}
            color={semanticColors.onDark.text.primary}
            strokeWidth={1.9}
          />
        )
      }
    />
  );
}

export function Hero({
  title,
  summary,
  eyebrow,
  metrics,
  metricVariant = "cards",
  metricsFillRow = true,
  backgroundSource,
  animateBackground = true,
  leading,
  primaryAction,
  secondaryAction,
  menuActions,
  onPress,
  onPressAccessibilityLabel,
  compactAction,
  bottomContent,
  style,
}: HeroProps) {
  const PrimaryActionIcon = primaryAction?.icon;
  const captureHeroPress = usePressCapture("Hero");
  const hasMetrics = !!metrics && metrics.length > 0;
  const hasPrimary = !!primaryAction;
  const renderInline = compactAction && hasMetrics && hasPrimary;

  const renderTextSection = (children: ReactNode) =>
    onPress ? (
      <Pressable
        onPress={() => {
          captureHeroPress(pressLabel(title), "hero_summary");
          onPress();
        }}
        accessibilityRole="button"
        accessibilityLabel={onPressAccessibilityLabel ?? title}
        style={({ pressed }) => ({
          flex: 1,
          minWidth: 0,
          opacity: pressed ? 0.72 : 1,
        })}
      >
        {children}
      </Pressable>
    ) : (
      <View style={{ flex: 1, minWidth: 0 }}>{children}</View>
    );
  return (
    <View
      style={[
        {
          borderRadius: 14,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: semanticColors.onDark.fill.subtle,
        },
        style,
      ]}
    >
      <HeroBackground source={backgroundSource} animate={animateBackground} />
      <View
        style={{ paddingHorizontal: 20, paddingTop: 20, paddingBottom: 20 }}
      >
        {leading ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 14,
            }}
          >
            {leading}
            {renderTextSection(
              <>
                {eyebrow ? <HeroEyebrowRow eyebrow={eyebrow} /> : null}
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      fontSize: 20,
                      lineHeight: 25,
                      color: semanticColors.onDark.text.primary,
                      fontWeight: "800",
                      paddingRight: 4,
                    }}
                  >
                    {title}
                  </Text>
                  {!onPress ? <HeroActionsMenu actions={menuActions} /> : null}
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12.5,
                    lineHeight: 18,
                    color: semanticColors.onDark.text.secondary,
                    fontWeight: "500",
                    marginTop: 7,
                  }}
                >
                  {summary}
                </Text>
              </>,
            )}
            {onPress ? <HeroActionsMenu actions={menuActions} /> : null}
          </View>
        ) : onPress ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            {renderTextSection(
              <>
                {eyebrow ? <HeroEyebrowRow eyebrow={eyebrow} /> : null}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 20,
                    lineHeight: 25,
                    color: semanticColors.onDark.text.primary,
                    fontWeight: "800",
                    paddingRight: 4,
                  }}
                >
                  {title}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12.5,
                    lineHeight: 18,
                    color: semanticColors.onDark.text.secondary,
                    fontWeight: "500",
                    marginTop: 7,
                  }}
                >
                  {summary}
                </Text>
              </>,
            )}
            <HeroActionsMenu actions={menuActions} />
          </View>
        ) : (
          <>
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <View style={{ flex: 1 }}>
                {eyebrow ? <HeroEyebrowRow eyebrow={eyebrow} /> : null}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 20,
                    lineHeight: 25,
                    color: semanticColors.onDark.text.primary,
                    fontWeight: "800",
                    paddingRight: 4,
                  }}
                >
                  {title}
                </Text>
              </View>
              <HeroActionsMenu actions={menuActions} />
            </View>

            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12.5,
                lineHeight: 18,
                color: semanticColors.onDark.text.secondary,
                fontWeight: "500",
                marginTop: 7,
              }}
            >
              {summary}
            </Text>
          </>
        )}

        {renderInline ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              marginTop: 15,
            }}
          >
            {metrics!.map((metric) => (
              <HeroMetricTag key={metric.label} metric={metric} />
            ))}
            <View style={{ flex: 1 }}>
              <PrimaryActionButton
                action={primaryAction!}
                Icon={PrimaryActionIcon}
              />
            </View>
          </View>
        ) : (
          <>
            {hasMetrics ? (
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: metricVariant === "tags" ? 15 : 16,
                }}
              >
                {metrics!.map((metric) =>
                  metricVariant === "tags" ? (
                    <HeroMetricTag key={metric.label} metric={metric} />
                  ) : (
                    <HeroMetricPill
                      key={metric.label}
                      metric={metric}
                      fill={metricsFillRow}
                    />
                  ),
                )}
              </View>
            ) : null}

            {primaryAction ? (
              <View style={{ marginTop: 17, gap: 8 }}>
                <PrimaryActionButton
                  action={primaryAction}
                  Icon={PrimaryActionIcon}
                />
                {secondaryAction ? (
                  <PrimaryActionButton
                    action={secondaryAction}
                    Icon={secondaryAction.icon}
                    emphasis="outline"
                  />
                ) : null}
              </View>
            ) : null}
          </>
        )}

        {bottomContent}
      </View>
    </View>
  );
}
